import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Input, InputGroup, InputLeftElement } from '@chakra-ui/react';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import useDebouncedCallback from '@/utilities/customHooks/useDebouncedCallback';
import { useNavigate } from 'react-router-dom';

interface IProps {
  callbackOnSelect?: (searchValue: string) => void; // used to refresh global state if needed.
}

const SEARCH_DELEY_MS = 500;

/**
 * Generalised search molecule that works with both the admissions and cas workflow dashboards.
 * Changes QSP values without interfacing with global state.
 */
export const Search = ({ callbackOnSelect = () => {} }: IProps) => {
  const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const navigate = useNavigate();

  const currentSearch = params.get('search') || '';
  const [inputSearch, setInputSearch] = useState(currentSearch);

  useEffect(() => {
    // issue when switching uni's, search needs to be cleared
    if (inputSearch !== currentSearch) {
      setInputSearch(currentSearch);
    }
  }, [currentSearch]);

  const onSearch = useDebouncedCallback((newSearchValue: string) => {
    const freshParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    freshParams.set('page', '1');
    if (newSearchValue) {
      freshParams.set('search', newSearchValue);
    } else {
      freshParams.delete('search');
    }
    navigate({ search: freshParams.toString() });

    callbackOnSelect(newSearchValue);
  }, SEARCH_DELEY_MS);

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchValue: string = event.target.value;
    setInputSearch(newSearchValue);
    onSearch(newSearchValue);
  };

  return (
    <InputGroup
      size={{ base: 'md', md: 'sm' }}
      width={{ base: '8rem', md: '9rem' }}
      flexShrink={0}
      mr={{ base: 2, md: 0 }}
    >
      <InputLeftElement pointerEvents="none">
        <FontAwesomeIcon icon={faSearch} />
      </InputLeftElement>

      <Input
        aria-label="Search"
        id="search"
        type="search"
        placeholder="Search"
        value={inputSearch}
        onChange={onChange}
      />
    </InputGroup>
  );
};
